/**
 * Header state is DERIVED from the lines, never stored (ADR-005, docs/04).
 *
 * A header status is a lie the moment two of five items come back with different grades,
 * so this exists purely for display: one word for a shopper glancing at a tracking page.
 * Nothing in the system may branch on it — branch on line state.
 *
 * Pure. No I/O, no clock.
 */
import type { LineState, ReturnLine } from "@sirdab/returns-shared";

import { isSettledState, isTerminalState } from "./state-machine";

/**
 * One integer per state, chosen so that `min()` over a return's lines IS the header state:
 *
 *  - Unsettled states carry their progress along the happy path (0..6). Any unsettled
 *    line therefore beats every settled one, and the least advanced of them wins: the
 *    header shows what the return is still waiting on, never the rosiest line.
 *  - Settled states start at 10, ordered by severity: when every line is settled the
 *    header shows the outcome that most needs a human. A fully restocked return is the
 *    only quiet outcome.
 *
 * Exported because the Postgres store computes the same header state as
 * `MIN(CASE state ...)` inside the list query (SAI-1353): a merchant filters and sorts on
 * the header's group, and the SQL must agree with this function to the row. Generate the
 * CASE from this table; never hand-copy the numbers.
 */
export const HEADER_RANK: Readonly<Record<LineState, number>> = {
  requested: 0,
  approved: 1,
  // Past `approved` (an attempt was made) but short of `awaiting_pickup` (no AWB yet), so
  // it is what the return is waiting on whenever another line has moved ahead of it.
  booking_failed: 2,
  awaiting_pickup: 3,
  in_transit: 4,
  received: 5,
  inspected: 6,
  lost_in_transit: 10,
  disposed: 11,
  quarantined: 12,
  rejected: 13,
  expired: 14,
  // Below `rejected` and `expired`: a shopper who withdrew their own return needs nobody's
  // attention, but it still outranks `restocked` because no goods came back.
  cancelled: 15,
  // The merchant closed it after review: settled, and quieter than every outcome except
  // a full restock. Matches the backend's SQL CASE (completed=16, restocked=17).
  completed: 16,
  restocked: 17,
};

const STATE_OF_RANK: ReadonlyMap<number, LineState> = new Map(
  (Object.entries(HEADER_RANK) as [LineState, number][]).map(
    ([state, rank]) => [rank, state],
  ),
);

/** Inverse of `HEADER_RANK`. Throws on a number the table never produced. */
export function headerStateOfRank(rank: number): LineState {
  const state = STATE_OF_RANK.get(rank);
  if (state === undefined) {
    throw new DeriveHeaderStateError(`no line state has header rank ${rank}`);
  }
  return state;
}

export class DeriveHeaderStateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DeriveHeaderStateError";
  }
}

/**
 * One state to show above a list of lines.
 *
 * - Any line still in flight -> the least advanced of those (what we are waiting on).
 * - All lines settled -> the most severe outcome present.
 */
export function deriveHeaderState(lines: readonly ReturnLine[]): LineState {
  if (lines.length === 0) {
    throw new DeriveHeaderStateError(
      "cannot derive a header state from zero lines",
    );
  }

  return headerStateOfRank(
    Math.min(...lines.map((line) => HEADER_RANK[line.state])),
  );
}

export interface HeaderSummary {
  readonly state: LineState;
  /** True only when every line has reached a terminal or exit state. */
  readonly isSettled: boolean;
  /** True when the lines are not all in the same state — a partial return. */
  readonly isPartial: boolean;
  /** How many lines sit in each state. The honest answer, when there is room for it. */
  readonly counts: Readonly<Partial<Record<LineState, number>>>;
}

/** The derivation plus the per-state counts a merchant queue actually wants. */
export function deriveHeaderSummary(
  lines: readonly ReturnLine[],
): HeaderSummary {
  const state = deriveHeaderState(lines);
  const counts: Partial<Record<LineState, number>> = {};
  for (const line of lines) counts[line.state] = (counts[line.state] ?? 0) + 1;

  return {
    state,
    isSettled: lines.every((line) => isSettledState(line.state)),
    isPartial: Object.keys(counts).length > 1,
    counts,
  };
}

/** Is the whole return finished with goods still in hand (nothing lost, nothing expired)? */
export function isFullyResolved(lines: readonly ReturnLine[]): boolean {
  return lines.length > 0 && lines.every((line) => isTerminalState(line.state));
}
